/**
 * Shared workflow logic for Field Report forms (Locate / PreCon / Soft Dig).
 *
 * Mirrors backend rules in /backend/utils/field_report_workflow.py — keep both
 * sides in sync when changing edit-permission rules.
 */

export const STATUS_DRAFT = 'draft';
export const STATUS_PENDING = 'submitted_pending_review';
export const STATUS_COMPLETED = 'completed';

/**
 * Compute who can do what on a given field-report record.
 *
 *   isCompleted      — true when status==='completed' (approved)
 *   isPending        — true when status==='submitted_pending_review'
 *   isDraft          — true when status==='draft' (or missing)
 *   isSubmitter      — current user originally submitted this report
 *   canApprove       — current user has approver permission
 *   isAdminOrSuper   — current user is admin or super_user
 *   isViewOnly       — UI should disable all inputs
 *   submitterSignatureLocked — submitter can edit but NOT their own signature
 */
export function computeFieldReportPerms({ formData, user, viewParamSet, canApprove }) {
  const status = (formData?.status || STATUS_DRAFT).toLowerCase();
  const isCompleted = status === STATUS_COMPLETED;
  const isPending = status === STATUS_PENDING;
  const isDraft = status === STATUS_DRAFT;

  const role = user?.role;
  const isAdminOrSuper = role === 'admin' || role === 'super_user';

  const submitterIds = [
    formData?.submitted_by_id,
    formData?.created_by_id,
  ].filter(Boolean);
  const isSubmitter = !!(user?.id && submitterIds.includes(user.id));

  let isViewOnly;
  if (viewParamSet) {
    isViewOnly = true;
  } else if (isCompleted) {
    // approved -> locked except admin/super_user
    isViewOnly = !isAdminOrSuper;
  } else if (isPending) {
    // submitter OR approver may still edit
    isViewOnly = !(canApprove || isSubmitter || isAdminOrSuper);
  } else {
    isViewOnly = false;
  }

  // While pending review, the submitter's signature stays locked (audit).
  // Approvers and admins can still overwrite it if they need to fix the record.
  const submitterSignatureLocked =
    isPending && isSubmitter && !canApprove && !isAdminOrSuper;

  // Admin "Edit while viewing" affordance.
  // Show the Edit Report button to approvers/admins for any non-draft
  // report so they have a visible affordance whether the form is
  // currently locked OR they're mid-edit. Clicking the button toggles
  // the ?view=true URL flag (lock ↔ unlock).
  const canAdminUnlock =
    (canApprove || isAdminOrSuper) && (isPending || (isCompleted && isAdminOrSuper));

  // After an admin clicks "Edit Report" the URL drops ?view=true so the
  // form unlocks — but the regular Save Draft / Submit buttons stay hidden
  // because the record is not a draft. isAdminEditingNonDraft is true in
  // exactly that window (approver/admin, currently editable, not a draft)
  // and powers a dedicated "Save Changes" button that PUTs the record
  // without changing its status.
  const isAdminEditingNonDraft =
    !isViewOnly && (canApprove || isAdminOrSuper) && !isDraft;

  return {
    status,
    isCompleted,
    isPending,
    isDraft,
    isSubmitter,
    canApprove: !!canApprove,
    isAdminOrSuper,
    isViewOnly,
    submitterSignatureLocked,
    canAdminUnlock,
    isAdminEditingNonDraft,
  };
}

/**
 * Banner text for the reviewer when photos are still uploading after submit.
 */
export function photoReadinessLabel({ expected, received, photos_ready }) {
  const exp = Number(expected) || 0;
  const rec = Number(received) || 0;
  if (photos_ready === true) return null;
  if (exp === 0 && rec === 0) return null;
  if (rec < exp) {
    return `${rec} of ${exp} photos uploaded — please wait before approving`;
  }
  return null;
}

/**
 * Color tag used in list views: green (ready), amber (still uploading),
 * or null (no signal).
 */
export function photoReadinessTone(record) {
  if (!record) return null;
  const exp = Number(record.expected_photos_count) || 0;
  const rec = Number(record.received_photos_count) || 0;
  const ready = record.photos_ready;
  if (exp === 0 && rec === 0 && ready == null) return null;
  if (ready === true) return 'ready';
  if (ready === false || rec < exp) return 'pending';
  return null;
}
